import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { visibleSections } from "@/lib/menu";
import { Logo } from "@/components/Logo";
import type { Role } from "@/api/auth";

interface SidebarProps {
  role: Role | undefined;
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ role, collapsed, onToggle }: SidebarProps) {
  const { t } = useTranslation();
  const sections = visibleSections(role);

  return (
    <aside
      className={cn(
        "h-screen sticky top-0 flex flex-col border-r border-slate-200 bg-white",
        "transition-[width] duration-200 ease-out",
        collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-3 border-b border-slate-200">
        {collapsed ? (
          <Logo variant="mark" height={32} className="mx-auto" />
        ) : (
          <div className="flex items-center gap-2">
            <Logo variant="mark" height={32} />
            <Logo variant="wordmark" height={26} />
          </div>
        )}
      </div>

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto py-2">
        {sections.map((section, idx) => (
          <div key={idx} className="mb-2">
            {section.titleKey && !collapsed && (
              <div className="px-4 pt-3 pb-1 text-xs uppercase tracking-wider text-slate-400">
                {t(section.titleKey)}
              </div>
            )}
            {section.titleKey && collapsed && idx > 0 && (
              <div className="border-t border-slate-200 mx-2 my-2" />
            )}
            <ul>
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === "/"}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 px-3 mx-2 my-0.5 py-2 rounded-md text-sm",
                          "transition-colors",
                          collapsed ? "justify-center" : "",
                          isActive
                            ? "bg-brand-navy-50 text-brand-navy-700 font-medium"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                        )
                      }
                      title={collapsed ? t(item.i18nKey) : undefined}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {!collapsed && <span className="truncate">{t(item.i18nKey)}</span>}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Collapse toggle */}
      <button
        type="button"
        onClick={onToggle}
        aria-label="Toggle sidebar"
        className="h-10 border-t border-slate-200 flex items-center justify-center
                   text-slate-500 hover:bg-slate-50 hover:text-slate-700"
      >
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>
    </aside>
  );
}
